import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Define the shape of a transcription metric
export interface TranscriptionMetric {
  id: string;
  transcriptionLength: number;
  createdAt: string;
}

// Define the shape of the savings settings
export interface SavingsSettings {
  hourlyRate: number;
  currency: string;
  minutesPerTranscription: number;
}

// Define the state shape
interface SavingsState {
  settings: SavingsSettings;
  transcriptionMetrics: TranscriptionMetric[];
}

// Default minutes per transcription
const DEFAULT_MINUTES_PER_TRANSCRIPTION = 20;

// Initial state
const initialState: SavingsState = {
  settings: {
    hourlyRate: 50,
    currency: 'USD',
    minutesPerTranscription: DEFAULT_MINUTES_PER_TRANSCRIPTION
  },
  transcriptionMetrics: []
};

// Create the slice
export const savingsSlice = createSlice({
  name: 'savings',
  initialState,
  reducers: {
    // Update settings
    updateSavingsSettings: (state, action: PayloadAction<Partial<SavingsSettings>>) => {
      state.settings = { ...state.settings, ...action.payload };
    },
    
    // Set transcription metrics
    setTranscriptionMetrics: (state, action: PayloadAction<TranscriptionMetric[]>) => {
      state.transcriptionMetrics = action.payload;
    },
    
    // Add a new transcription metric
    addTranscriptionMetric: (state, action: PayloadAction<TranscriptionMetric>) => {
      state.transcriptionMetrics.push(action.payload);
    }
  },
});

// Export actions
export const { 
  updateSavingsSettings, 
  setTranscriptionMetrics,
  addTranscriptionMetric
} = savingsSlice.actions;

// Helper function to format time from seconds
export const formatTimeFromSeconds = (seconds: number): string => {
  if (seconds === 0) return '0m';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  let result = '';
  if (hours > 0) result += `${hours}h `;
  if (minutes > 0 || hours === 0) result += `${minutes}m`;
  
  return result.trim();
};

// Helper function to calculate cost saving
export const calculateCostSaving = (seconds: number, hourlyRate: number): number => {
  const hours = seconds / 3600;
  return hours * hourlyRate;
};

// Calculate total time saved - kept for backward compatibility
export const calculateTotalTimeSaved = (metrics: TranscriptionMetric[]): number => {
  return metrics.length * 20 * 60; // 20 minutes per transcription, converted to seconds
};

// Calculate today's time saved - kept for backward compatibility
export const calculateTodayTimeSaved = (metrics: TranscriptionMetric[]): number => {
  const today = new Date();
  
  // Filter metrics for today
  const todayMetrics = metrics.filter(metric => {
    const metricDate = new Date(metric.createdAt);
    return metricDate.getDate() === today.getDate() &&
           metricDate.getMonth() === today.getMonth() &&
           metricDate.getFullYear() === today.getFullYear();
  });
  
  return todayMetrics.length * 20 * 60; // 20 minutes per transcription, converted to seconds
};

// Format time values in a user-friendly format with "mins" and "hrs"
export const formatTimeDisplayForNavbar = (seconds: number): string => {
  // Convert to minutes
  let minutes = Math.ceil(seconds / 60);
  
  // If over 10 minutes, round up to the nearest 10
  if (minutes > 10) {
    minutes = Math.ceil(minutes / 10) * 10;
  }
  
  // If it's less than 60 minutes, display in minutes
  if (minutes < 60) {
    return `${minutes} mins`;
  }
  
  // Convert to hours with decimal for display
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  // If there are no remaining minutes, just show hours
  if (remainingMinutes === 0) {
    return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
  }
  
  // Otherwise show hours and minutes
  return `${hours} ${hours === 1 ? 'hr' : 'hrs'} ${remainingMinutes} mins`;
}

export default savingsSlice.reducer; 